import React from 'react';
import { Box, Table, IconButton, colors } from '@neshane/blueberry';
import { OperationStatusBadge } from '../StatusBadges';
import { OPERATION_TYPE_LABELS, PROVIDER_LABELS, formatDateTime } from '../../labels';

export function OperationsTable({ operations, filteredOperations, total, onRetry, onCancel, busyId }) {
    const data = filteredOperations ?? operations ?? [];

    const canRetry = op => ['FAILED', 'CANCELLED'].includes(op.status);
    const canCancel = op => !['SUCCEEDED', 'DEAD_LETTERED', 'CANCELLED'].includes(op.status);

    const columns = [
        {
            id: 'operationType',
            header: 'عملیات',
            align: 'right',
            width: '220px',
            renderCell: op => (
                <div className="flex flex-col gap-2">
                    <span className="body-m text-primary">
                        {OPERATION_TYPE_LABELS[op.operationType] || op.operationType}
                    </span>
                    <span className="body-s text-secondary-bg-primary" dir="ltr">
                        {op.operationId}
                    </span>
                </div>
            ),
            getSortValue: op => OPERATION_TYPE_LABELS[op.operationType] || op.operationType,
        },
        {
            id: 'provider',
            header: 'سرویس',
            align: 'right',
            width: '150px',
            renderCell: op => (
                <span className="body-m text-primary">{PROVIDER_LABELS[op.provider] || op.provider}</span>
            ),
            getSortValue: op => PROVIDER_LABELS[op.provider] || op.provider,
        },
        {
            id: 'userId',
            header: 'کاربر',
            align: 'right',
            width: '80px',
            renderCell: op => <span className="body-m text-primary">{op.userId ?? '—'}</span>,
            getSortValue: op => op.userId ?? 0,
        },
        {
            id: 'attempts',
            header: 'تلاش',
            align: 'center',
            width: '80px',
            renderCell: op => (
                <span className="body-m text-primary">
                    {op.attemptCount}/{op.maxAttempts}
                </span>
            ),
            getSortValue: op => op.attemptCount,
        },
        {
            id: 'status',
            header: 'وضعیت',
            align: 'center',
            width: '120px',
            renderCell: op => <OperationStatusBadge status={op.status} />,
            getSortValue: op => op.status,
        },
        {
            id: 'createdAt',
            header: 'زمان ثبت',
            align: 'right',
            width: '160px',
            renderCell: op => <span className="body-m text-primary">{formatDateTime(op.createdAt)}</span>,
            getSortValue: op => op.createdAt,
        },
        {
            id: 'actions',
            header: 'عملیات',
            width: '120px',
            renderCell: op => (
                <div className="flex items-center gap-4">
                    <IconButton
                        variant="tertiaryNeutral"
                        iconName="arrow-path"
                        iconColor={colors.contentBrandDefault}
                        onClick={() => onRetry?.(op.id, 'retry')}
                        aria-label="اجرای مجدد عملیات"
                        isDisabled={!canRetry(op) || busyId === op.id}
                    />
                    <IconButton
                        variant="tertiaryNeutral"
                        iconName="circle-strikeout"
                        iconColor={colors.contentPrimary}
                        onClick={() => onCancel?.(op.id, 'cancel')}
                        aria-label="لغو عملیات"
                        isDisabled={!canCancel(op) || busyId === op.id}
                    />
                </div>
            ),
        },
    ];

    return (
        <Box className="flex flex-col gap-16">
            <Table
                columns={columns}
                data={data}
                rowHeight="compact"
                getRowId={op => String(op.id)}
                emptyState={{
                    title: 'عملیاتی در صف نیست',
                    subtitle: 'هنوز هیچ عملیات خارجی در صف ثبت نشده است',
                }}
                footer={
                    data.length > 0 && total != null ? (
                        <div className="flex items-center justify-between px-16 py-8">
                            <span className="body-s text-secondary-bg-primary">
                                {data.length} عملیات از مجموع {total}
                            </span>
                        </div>
                    ) : undefined
                }
            />
        </Box>
    );
}

export default OperationsTable;
